import React, { useState } from "react";
import { contractRepo, customerRepo } from "../../services";
import { useApp } from "../../context/AppContext";
import { useI18n } from "../../i18n";
import { Button, Card, DataTable, Drawer, Input, Modal, PageHead, Select, SignaturePad, StatusBadge, Textarea, baht, formatDate, useAsync } from "../../components/ui";

const DEFAULT_TERMS = [
  "ผู้เช่า/ผู้ซื้อ ตกลงชำระค่าบริการตามกำหนดที่ระบุในสัญญา",
  "กรณีผิดนัดชำระเกิน 7 วัน ผู้ให้บริการมีสิทธิ์ระงับการให้บริการ",
  "ความเสียหายที่เกิดจากการใช้งานผิดวิธี ผู้เช่าเป็นผู้รับผิดชอบ",
].join("\n");
const dateInput = (value) => (value ? String(value).slice(0, 10) : "");
const EMPTY = { id: "", customerId: "", title: "", value: "", startAt: "", endAt: "", terms: DEFAULT_TERMS };

export default function Contracts() {
  const { orgId, branchId, branches, organization, profile, toast } = useApp();
  const { tx } = useI18n();
  const { data: contracts, loading, reload } = useAsync(() => contractRepo.all(orgId), [orgId], []);
  const [active, setActive] = useState(null);
  const [signature, setSignature] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const { data: customers } = useAsync(() => customerRepo.all(orgId), [orgId], []);

  const run = async (job, done) => {
    setSaving(true);
    try {
      await job();
      done();
    } catch (error) {
      toast(error?.message || tx("บันทึกไม่สำเร็จ", "Save failed"), "error");
    } finally {
      setSaving(false);
    }
  };

  const nextDocNo = () => {
    const max = (contracts || []).reduce((m, row) => Math.max(m, Number(String(row.docNo || "").replace(/\D/g, "")) || 0), 7000);
    return `CT-${max + 1}`;
  };

  const openForm = (row) =>
    setForm(
      row
        ? { ...EMPTY, ...row, value: String(row.value ?? ""), startAt: dateInput(row.startAt), endAt: dateInput(row.endAt), terms: (row.terms || []).join("\n") }
        : { ...EMPTY, startAt: new Date().toISOString().slice(0, 10) }
    );

  const saveForm = () =>
    run(async () => {
      const customer = (customers || []).find((c) => c.id === form.customerId);
      const value = Number(form.value);
      const terms = form.terms.split("\n").map((line) => line.trim()).filter(Boolean);
      if (!customer) throw new Error(tx("เลือกลูกค้า", "Select a customer"));
      if (!form.title.trim()) throw new Error(tx("กรอกชื่อสัญญา", "Title required"));
      if (!(value >= 0) || form.value === "") throw new Error(tx("กรอกมูลค่าสัญญา (ไม่ติดลบ)", "Enter a valid contract value"));
      if (!terms.length) throw new Error(tx("ใส่เงื่อนไขสัญญาอย่างน้อย 1 ข้อ", "At least one term required"));
      if (form.startAt && form.endAt && form.endAt < form.startAt) throw new Error(tx("วันสิ้นสุดต้องไม่ก่อนวันเริ่ม", "End date is before start date"));
      const payload = {
        title: form.title.trim(),
        customerId: customer.id,
        customerName: customer.name,
        value,
        startAt: form.startAt || null,
        endAt: form.endAt || null,
        terms,
      };
      if (form.id) {
        const saved = await contractRepo.update(orgId, form.id, payload);
        setActive((prev) => (prev && prev.id === form.id ? { ...prev, ...payload, ...(saved || {}) } : prev));
      } else {
        await contractRepo.create(orgId, {
          ...payload,
          docNo: nextDocNo(),
          status: "draft",
          signedAt: null,
          signedBy: null,
          assetId: null,
          branchId: customer.branchId || branchId || branches?.[0]?.id || "",
          createdAt: new Date().toISOString(),
        });
      }
    }, () => {
      setForm(null);
      reload();
      toast(tx("บันทึกร่างสัญญาแล้ว", "Contract saved"), "success");
    });

  const sendToCustomer = async () => {
    if (!active) return;
    await run(async () => {
      await contractRepo.update(orgId, active.id, { status: "waiting_sign", sentAt: new Date().toISOString() });
      setActive(null);
    }, () => {
      reload();
      toast(tx("ส่งสัญญาให้ลูกค้าเซ็นแล้ว ลูกค้าเซ็นได้ที่หน้า สัญญา & ค่างวด", "Sent — the customer can sign under Contracts"), "success");
    });
  };

  // ลายเซ็นฝั่งร้าน — แยกจากลายเซ็นลูกค้า (signedBy/signature เป็นของลูกค้าเท่านั้น)
  const confirmShopSign = async () => {
    if (!active || !signature) return toast(tx("กรุณาเซ็นชื่อก่อนยืนยัน", "Please sign before confirming"), "error");
    await run(async () => {
      const patch = { shopSignedAt: new Date().toISOString(), shopSignedBy: profile?.fullname || organization?.name || "", shopSignature: signature };
      await contractRepo.update(orgId, active.id, patch);
      setActive((prev) => ({ ...prev, ...patch }));
    }, () => {
      setSignature(null);
      reload();
      toast(tx("เซ็นในนามร้านเรียบร้อย", "Signed on behalf of the shop"), "success");
    });
  };

  return (
    <>
      <PageHead
        title={tx("สัญญาอิเล็กทรอนิกส์", "e-Contracts")}
        subtitle={tx("ร่างสัญญาและเก็บลายเซ็นดิจิทัลของลูกค้า", "Draft contracts and collect digital signatures")}
        actions={<Button variant="primary" onClick={() => openForm(null)}>+ {tx("สร้างสัญญา", "New contract")}</Button>}
      />

      <DataTable
        loading={loading}
        rows={contracts || []}
        onRowClick={setActive}
        emptyText={tx("ยังไม่มีสัญญา", "No contracts")}
        columns={[
          { key: "docNo", label: tx("เลขที่", "No.") },
          { key: "title", label: tx("ชื่อสัญญา", "Title") },
          { key: "customerName", label: tx("ลูกค้า", "Customer") },
          { key: "value", label: tx("มูลค่า", "Value"), align: "right", render: (row) => baht(row.value, 0) },
          { key: "endAt", label: tx("สิ้นสุด", "End"), render: (row) => formatDate(row.endAt) },
          { key: "status", label: tx("สถานะ", "Status"), render: (row) => <StatusBadge status={row.status} /> },
        ]}
      />

      <Drawer
        open={!!active}
        title={active?.docNo}
        onClose={() => { setActive(null); setSignature(null); }}
        footer={
          active?.status === "draft" ? (
            <>
              <Button onClick={() => openForm(active)}>{tx("แก้ไขร่าง", "Edit draft")}</Button>
              <Button variant="primary" disabled={saving} onClick={sendToCustomer}>{tx("ส่งให้ลูกค้าเซ็น", "Send for signature")}</Button>
            </>
          ) : null
        }
      >
        {active ? (
          <>
            <Card title={active.title}>
              <div className="u-fs-13" style={{ display: "grid", gap: 6 }}>
                <div className="u-between"><span className="u-muted">{tx("ลูกค้า", "Customer")}</span><span>{active.customerName}</span></div>
                <div className="u-between"><span className="u-muted">{tx("มูลค่า", "Value")}</span><span className="u-strong">{baht(active.value, 0)}</span></div>
                <div className="u-between"><span className="u-muted">{tx("เริ่ม", "Start")}</span><span>{formatDate(active.startAt)}</span></div>
                <div className="u-between"><span className="u-muted">{tx("สิ้นสุด", "End")}</span><span>{formatDate(active.endAt)}</span></div>
                <div className="u-between"><span className="u-muted">{tx("สถานะ", "Status")}</span><StatusBadge status={active.status} /></div>
              </div>
            </Card>

            <Card title={tx("เงื่อนไขสัญญา", "Terms")}>
              <ol className="u-fs-13" style={{ paddingLeft: 18, margin: 0 }}>
                {(active.terms || []).map((term, index) => (
                  <li key={index} style={{ marginBottom: 6 }}>{term}</li>
                ))}
              </ol>
            </Card>

            {active.signedAt ? (
              <Card title={tx("ลูกค้าลงนามแล้ว", "Signed by customer")}>
                <div className="u-fs-13">{tx("ลงนามโดย", "Signed by")} {active.signedBy} · {formatDate(active.signedAt, true)}</div>
              </Card>
            ) : active.status === "waiting_sign" ? (
              <Card title={tx("รอลูกค้าลงนาม", "Waiting for customer signature")}>
                <div className="u-fs-13 u-muted">{tx("ลูกค้าเซ็นเองได้ที่หน้า สัญญา & ค่างวด ฝั่งลูกค้า", "The customer signs from their own Contracts page")}</div>
              </Card>
            ) : null}

            {active.shopSignedAt ? (
              <Card title={tx("ร้านลงนามแล้ว", "Signed by shop")}>
                <div className="u-fs-13">{tx("ลงนามโดย", "Signed by")} {active.shopSignedBy || "-"} · {formatDate(active.shopSignedAt, true)}</div>
              </Card>
            ) : ["draft", "waiting_sign", "active"].includes(active.status) ? (
              <Card title={tx("ลงลายเซ็นในนามร้าน", "Sign on behalf of the shop")}>
                <SignaturePad onChange={setSignature} />
                <Button variant="primary" block className="u-mt-16" disabled={saving} onClick={confirmShopSign}>
                  {tx("ยืนยันการลงนามของร้าน", "Confirm shop signature")}
                </Button>
              </Card>
            ) : null}
          </>
        ) : null}
      </Drawer>

      <Modal
        open={!!form}
        title={form?.id ? tx("แก้ไขร่างสัญญา", "Edit draft contract") : tx("สร้างสัญญา", "New contract")}
        onClose={() => setForm(null)}
        footer={<><Button onClick={() => setForm(null)}>{tx("ยกเลิก", "Cancel")}</Button><Button variant="primary" disabled={saving} onClick={saveForm}>{tx("บันทึกร่าง", "Save draft")}</Button></>}
      >
        {form ? (
          <>
            <Select
              label={tx("ลูกค้า", "Customer")}
              placeholder={tx("เลือกลูกค้า", "Select customer")}
              options={(customers || []).map((c) => ({ value: c.id, label: `${c.name} · ${c.phone || c.code || ""}` }))}
              value={form.customerId}
              onChange={(e) => setForm({ ...form, customerId: e.target.value })}
            />
            <Input label={tx("ชื่อสัญญา", "Title")} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <Input label={tx("มูลค่าสัญญา (บาท)", "Value (THB)")} type="number" min="0" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} />
            <Input label={tx("วันที่เริ่ม", "Start")} type="date" value={form.startAt} onChange={(e) => setForm({ ...form, startAt: e.target.value })} />
            <Input label={tx("วันที่สิ้นสุด", "End")} type="date" value={form.endAt} onChange={(e) => setForm({ ...form, endAt: e.target.value })} />
            <Textarea label={tx("เงื่อนไขสัญญา (บรรทัดละ 1 ข้อ)", "Terms (one per line)")} rows={6} value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} />
          </>
        ) : null}
      </Modal>
    </>
  );
}
